import React, { ReactNode, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Form, Pagination, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { SaveOutlined, StopOutlined } from '@ant-design/icons';
import { EditButton } from '../../CustomButtons';
import { CardLayout, cardLayoutOptions, viewOption } from '../../Layout';
import { LinkComponent } from '../../LinkComponent';
import { ShowDataBytes } from '../../SmallComponents';
import cx from 'classnames';
import { FieldData } from 'rc-field-form/es/interface';
import { appConfig } from '../../../settings';
import { IGetApiType } from '../../../REST';
import { ITariffPackageInstanceCardItem } from '../../../interfaces';
import {
    useTariffPackageInstanceCardList,
    useTariffPackageInstanceCardListInfo,
    useTariffPackageInstanceItem,
    useUiIsSpinnerFound,
} from '../../../hooks';
import {
    apiMessagesDeleteMessageFormFieldName,
    apiMessagesSaveMessages,
    getTariffPackageInstancesCards,
    patchCard,
    setTariffPackageInstanceCardListPage,
    setTariffPackageInstanceCardListPageSize,
} from '../../../middleware';
import { cleanColumnsDataSource } from '../../../instruments';
import { tF, tI } from '../../../translate';
import { FilterPageSize } from '../../FilterToolbar/Components';
import { FieldDataVolumeUnit } from '../../Forms';
import IconStyles from '../../IconComponents/m_styles.less';
import ViewStyles from '../../styles/m_viewStyles.less';

type IDataType = ITariffPackageInstanceCardItem & {
    key?: string;
    nameLink?: ReactNode;
    usageLimitField?: ReactNode;
    toolBar?: ReactNode;
};

export const ShowTariffPackageInstanceCards: React.FC = observer(() => {
    const loadingTypes: IGetApiType[] = [
        'GET_DATA_UNIT_TYPE_SELECT',
        'GET_TARIFF_PACKAGE_INSTANCE',
        'GET_TARIFF_PACKAGE_INSTANCE_CARDS',
        'PATCH_CARD',
    ];
    const [form] = Form.useForm();
    const [editId, setEditId] = useState<number | undefined>();
    const [isValuesChange, setIsValuesChange] = useState<boolean>(false);
    const isLoading = useUiIsSpinnerFound(loadingTypes);
    const { id } = useTariffPackageInstanceItem();
    const { allCount, pageSize, pageNumber } = useTariffPackageInstanceCardListInfo();
    const list = useTariffPackageInstanceCardList();

    useEffect(() => {
        if (id) {
            getTariffPackageInstancesCards();
        }
    }, [id]);

    useEffect(() => {
        const currentItem = list.find((i: ITariffPackageInstanceCardItem) => i.id === editId);
        if (currentItem) {
            const initialValues = {
                usageLimitInUnit: currentItem?.usageLimitInUnit,
                usageLimitUnit: currentItem?.usageLimitUnit,
            };
            setTimeout(() => {
                form.setFieldsValue(initialValues);
            }, 250);
        }
    }, [editId]);

    const startEditCard = (id: number) => {
        setEditId(id);
        setIsValuesChange(false);
    };

    const canselEditCard = () => {
        setEditId(undefined);
    };

    const saveCard = async (id: number) => {
        const values = form.getFieldsValue();
        const { isError, errors } = await patchCard(id.toString(), values);
        if (isError) {
            apiMessagesSaveMessages(errors);
            form.validateFields();
        } else {
            setEditId(undefined);
            getTariffPackageInstancesCards();
        }
    };

    const onValuesChange = (changedFields: FieldData[]) => {
        Object.keys(changedFields).forEach((key) => {
            apiMessagesDeleteMessageFormFieldName(key);
        });
        setIsValuesChange(true);
    };

    const tableColumns: ColumnsType<IDataType> = [
        {
            title: tF('Name'),
            dataIndex: 'nameLink',
        },
        {
            title: tF('ICCID'),
            dataIndex: 'iccid',
        },
        {
            title: tF('MSISDN'),
            dataIndex: 'msisdn',
        },
        {
            title: tF('Used Amount'),
            dataIndex: 'usedAmount',
        },
        {
            title: tF('Card data limit per month'),
            dataIndex: 'usageLimitField',
        },
        {
            title: '',
            dataIndex: 'toolBar',
            className: cx(ViewStyles.columnShortToolbar),
            width: viewOption.rateTableEdit.widthToolBarV2,
        },
    ];
    const tableData: IDataType[] = list.map((c: ITariffPackageInstanceCardItem) => {
        const initialValues = {
            usageLimitInUnit: c.usageLimitInUnit,
            usageLimitUnit: c.usageLimitUnit,
        };
        return {
            key: c.id.toString(),
            nameLink: <LinkComponent currentListComponent={'CardList'} id={c.id} name={c.name} />,
            ...c,
            usageLimitField:
                editId === c.id ? (
                    <Form
                        form={form}
                        name="cardEdit"
                        initialValues={initialValues}
                        scrollToFirstError={true}
                        layout={'vertical'}
                        className={'formStyle'}
                        onValuesChange={onValuesChange}
                    >
                        <FieldDataVolumeUnit
                            fields={{ limit: 'usageLimitInUnit', unit: 'usageLimitUnit' }}
                            form={form}
                            initialValues={initialValues}
                        />
                    </Form>
                ) : (
                    <div>
                        <ShowDataBytes bytes={c.usageLimitByte} noDataText={'Unlimited'} />
                    </div>
                ),
            toolBar:
                editId === c.id ? (
                    <div className={cx(ViewStyles.columnShortToolbar, ViewStyles.tableFieldAlign)}>
                        <Button
                            disabled={isLoading || !isValuesChange}
                            icon={
                                <SaveOutlined
                                    className={cx(IconStyles.buttonIcon, {
                                        [IconStyles.disabled]: isLoading || !isValuesChange,
                                    })}
                                />
                            }
                            onClick={() => saveCard(c.id)}
                            title={'Save'}
                            type={'primary'}
                        />
                        <Button
                            disabled={isLoading}
                            icon={<StopOutlined className={cx(IconStyles.buttonIcon, IconStyles.done)} />}
                            onClick={canselEditCard}
                            title={'Cancel'}
                        />
                    </div>
                ) : (
                    <div className={cx(ViewStyles.columnShortToolbar, ViewStyles.tableFieldAlign)}>
                        <EditButton
                            disabled={isLoading}
                            isHiddenTitle
                            onClick={(): void => {
                                startEditCard(c.id);
                            }}
                        />
                    </div>
                ),
        };
    });

    return (
        <CardLayout
            columnOption={cardLayoutOptions.cardTableFull}
            count={allCount}
            idSpinners={loadingTypes}
            isTable
            title={'Cards'}
            titleToolBar={
                <div className={ViewStyles.combinedToolBar}>
                    <div className={cx(ViewStyles.combinedFilterItem, ViewStyles.combinedFilterPageSize)}>
                        <FilterPageSize
                            isLocalChange
                            onChange={(n) => setTariffPackageInstanceCardListPageSize(n)}
                            value={pageSize}
                        />
                    </div>
                    <Pagination
                        className={'customPagination'}
                        current={pageNumber}
                        hideOnSinglePage
                        onChange={setTariffPackageInstanceCardListPage}
                        pageSize={pageSize}
                        pageSizeOptions={appConfig.pageDefaultParams.pageSizeValues.map((i: number) => i.toString())}
                        showSizeChanger={false}
                        total={allCount}
                        showTotal={(total: number, range: Array<number>): JSX.Element => (
                            <div>
                                {range[0]}-{range[1]} {tI('of')} {total} {tI('items')}
                            </div>
                        )}
                    />
                </div>
            }
        >
            <Table
                className={'table-theme'}
                {...cleanColumnsDataSource<IDataType>(tableColumns, tableData)}
                pagination={false}
                size={'middle'}
            />
        </CardLayout>
    );
});
